import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { navigateToMap } from "@shared/utils/utils";
import { ArrowLeft, Building, MapPin } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

interface FacilityProfileCardProps {
  data: {
    facilityNo: string;
    facilityName: string;
    facilityTypeDisplay: string;
    location: string;
    commissionedDate: string;
    latitude: string;
    longitude: string;
    areaDisplay: string;
    serviceZoneDisplay: string;
    userData?: {
      name?: string;
      email?: string;
    };
    description?: string;
    addressSiteLabel?: string;
  };
}

export const FacilityProfileCard: React.FC<FacilityProfileCardProps> = ({
  data,
}) => {
  const navigate = useNavigate();

  const infoFields = [
    { label: "Facility ID", value: data?.facilityNo },
    { label: "Facility Name", value: data?.facilityName },
    { label: "Facility Type", value: data?.facilityTypeDisplay },
    { label: "Commission Date", value: data?.commissionedDate },
    { label: "Contact Person", value: data?.userData?.name },
    { label: "Contact", value: data?.userData?.email },
    { label: "Zones / Divisions Served", value: data?.serviceZoneDisplay },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/ax/asset-registry/facilities")}
          />
          <Building className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-lg font-semibold">
            {data?.facilityNo}
          </CardTitle>
        </div>
        <p className="text-sm text-gray-600">
          Comprehensive facility information and operational details
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {infoFields.map((field) => (
            <div key={field.label}>
              <ValueLabel label={field.label} value={field.value} />
            </div>
          ))}
        </div>

        <div>
          <p className="text-xs text-muted-foreground tracking-wide">
            Location
          </p>
          <p className="text-sm font-medium text-foreground">
            {" "}
            {data?.addressSiteLabel || "NA"}
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 p-0 h-auto"
              onClick={() => navigateToMap(data?.latitude, data?.longitude)}
            >
              <MapPin className="h-4 w-4 mr-0" />
              View on map
            </Button>
          </p>
        </div>

        {/* Description */}
        {data?.description && (
          <div>
            <p className="text-xs text-muted-foreground tracking-wide">
              Description
            </p>
            <p className="text-sm font-medium text-foreground">
              {data.description}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
